import { type FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { setBonusCode } from '@/api/auth';
import type { LootboxBonus } from '@/api/common';
import { Button } from '@/components/buttons';
import { Modals } from '@/helpers/modals';
import { useNavigation } from '@/hooks/navigation';
import { useScratchGameLootbox } from '@/modules/promotions/hooks';
import { useGetCommonStore, useModalsStore } from '@/store';
import { REASON_FOR_UPDATING_USER } from '@/store/commonStore/slices/auth/helpers';
import { PromotionPageTabs } from '../../../PromotionsPage';

import { ScScratchActionButton } from './styled';

interface IScratchActionButton {
  bonus: LootboxBonus | null;
}

export const ScratchActionButton: FC<IScratchActionButton> = ({ bonus }) => {
  const { t } = useTranslation('scratchGame');
  const { ticketTheme, currentConfig } = useScratchGameLootbox();

  const { navigate } = useNavigation();
  const useCommonStore = useGetCommonStore();

  const fetchUser = useCommonStore.use.fetchUser();
  const open = useModalsStore.use.open();
  const { isReachedDailyLimit, isAuthenticated, lootbox, isFinished } =
    useScratchGameLootbox();

  const handleButtonClick = useCallback(async () => {
    if (!isAuthenticated) {
      open(Modals.Registration);
      return;
    }

    if (bonus) {
      navigate(`/promotions?tab=${PromotionPageTabs.ACTIVE_BONUSES}`);
      return;
    }

    if (isReachedDailyLimit) {
      navigate('/');
      return;
    }

    const { error } = await setBonusCode({
      depositBonusCode: currentConfig[ticketTheme].code,
    });

    if (error) {
      console.error(error);
      return;
    }

    await fetchUser(REASON_FOR_UPDATING_USER.DEPOSIT);
    open(Modals.Deposit);
  }, [
    isAuthenticated,
    navigate,
    open,
    fetchUser,
    currentConfig,
    isReachedDailyLimit,
    ticketTheme,
    bonus,
  ]);

  const btnText = useMemo(() => {
    switch (true) {
      case !isAuthenticated:
        return 'register';
      case !!bonus:
        return 'claimBonus';
      case isReachedDailyLimit:
        return 'goToLobby';
      default:
        return 'howToStart.button';
    }
  }, [isAuthenticated, isFinished, isReachedDailyLimit, bonus]);

  if (lootbox[ticketTheme] && !bonus && isAuthenticated) return null;

  return (
    <ScScratchActionButton>
      <Button onClick={handleButtonClick} primary wide>
        {t(`${btnText}`)}
      </Button>
    </ScScratchActionButton>
  );
};
